import {
  ArrowUpRight,
  Bot,
  Building2,
  Clock,
  Heart,
  MessageSquare,
  Search,
  Shield,
  Tag,
  TrendingUp,
  Users,
} from 'lucide-react'
import { SiGoogle, SiOpenai } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { aiVisibility } from 'sprites-types/workflow/tools/aiVisibility'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { ContentPreview } from '../shared/ContentPreview'
import { MetricPill } from '../shared/MetricPill'
import { ScoreCard } from '../shared/ScoreCard'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

// AI Platform Logo Component
function AiPlatformLogo({ platform }: { platform: string }) {
  const getPlatformIcon = (platformName: string) => {
    switch (platformName) {
      case 'ChatGPT':
        return <SiOpenai className="h-5 w-5 text-green-600" title="OpenAI ChatGPT" />
      case 'Gemini':
        return <SiGoogle className="h-5 w-5 text-blue-600" title="Google Gemini" />
      case 'Perplexity':
        return (
          <div
            className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white"
            title="Perplexity"
          >
            P
          </div>
        )
      default:
        return <Bot className="h-5 w-5 text-gray-600" />
    }
  }

  return (
    <div className="flex items-center gap-2">
      {getPlatformIcon(platform)}
      <span className="text-xs font-medium text-gray-700">{platform}</span>
    </div>
  )
}

function AiVisibilityDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(aiVisibility, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  // Handle both array and object results
  const dataItems = Array.isArray(result) ? result : [result]
  const data = dataItems[0] // Display first item, but preserve all data

  // Determine mode based on result structure
  const mode = inputs.mode || 'brand_report'
  const isScrapeMode = mode === 'scrape' || !!(data as any)?.extracted_content

  if (isScrapeMode) {
    return <ScrapeResultDisplay data={data} inputs={inputs} />
  } else {
    return <BrandReportDisplay data={data} inputs={inputs} />
  }
}

// Scrape Mode Display Component
function ScrapeResultDisplay({ data, inputs }: { data: any; inputs: any }) {
  const query = (data as any)?.query || inputs.query || ''
  const extractedContent = (data as any)?.extracted_content || ''
  const contentType = (data as any)?.content_type || 'text'
  const wordCount = (data as any)?.word_count || 0
  const hasCodeBlocks = (data as any)?.has_code_blocks || false
  const hasLinks = (data as any)?.has_links || false
  const extractedAt = (data as any)?.extracted_at || ''
  const requestedPlatform = (data as any)?.requestedPlatform || 'ChatGPT'

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="rounded-3xl border border-gray-200/50 bg-gradient-to-r from-blue-50/50 to-purple-50/50 p-6 shadow-sm backdrop-blur-[8px]">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 p-3 text-white shadow-lg">
              <MessageSquare className="h-6 w-6" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">AI Answer Extract</h2>
              <AiPlatformLogo platform={requestedPlatform} />
            </div>

            <div className="mb-4">
              <p className="mb-1 text-sm font-medium text-gray-700">Query:</p>
              <div className="rounded-xl border border-gray-200/60 bg-white/80 px-4 py-3">
                <p className="leading-relaxed text-gray-900">{query}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-2">
              <MetricPill
                count={wordCount}
                label="words"
                color="blue"
                variant="default"
                size="sm"
              />
              <MetricPill label={contentType} color="purple" variant="default" size="sm" />
              {hasCodeBlocks && (
                <MetricPill label="code" color="orange" variant="default" size="sm" />
              )}
              {hasLinks && <MetricPill label="links" color="green" variant="default" size="sm" />}
              {extractedAt && (
                <MetricPill
                  icon={<Clock className="h-3 w-3" />}
                  label={new Date(extractedAt).toLocaleDateString()}
                  color="gray"
                  variant="subtle"
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Preview */}
      <ContentPreview
        content={extractedContent}
        contentType={contentType as any}
        wordCount={wordCount}
        hasCodeBlocks={hasCodeBlocks}
        hasLinks={hasLinks}
        className="shadow-sm"
      />
    </div>
  )
}

// Brand Report Mode Display Component
function BrandReportDisplay({ data, inputs }: { data: any; inputs: any }) {
  const url = inputs.url || inputs.website || inputs.domain || ''
  const brandInput = (data as any)?.brandInput || inputs.brandInput || ''
  const brandAwarenessScore = (data as any)?.brand_awareness_score || 0
  const brandAwarenessCategory = (data as any)?.brand_awareness_category || ''
  const brandAwarenessSources = (data as any)?.brand_awareness_sources || []
  const brandDescription = (data as any)?.brand_description || ''
  const companyUrl = (data as any)?.company_url || url
  const sentimentClassification =
    (data as any)?.sentiment_classification || (data as any)?.sentiment_category || ''
  const credibilityClassification =
    (data as any)?.credibility_classification || (data as any)?.credibility_category || ''
  const servicesProducts = (data as any)?.services_products || []
  const relatedPrompts = (data as any)?.related_prompts || []
  const requestedPlatform = (data as any)?.requestedPlatform || inputs?.aiTool || 'ChatGPT'

  const getDomain = (urlStr: string) => {
    if (!urlStr) return ''
    try {
      return new URL(urlStr).hostname
    } catch {
      return urlStr
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="rounded-3xl border border-gray-200/50 bg-gradient-to-r from-purple-50/50 to-blue-50/50 p-6 shadow-sm backdrop-blur-[8px]">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 p-3 text-white shadow-lg">
              <Building2 className="h-6 w-6" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">Brand Visibility Report</h2>
              <AiPlatformLogo platform={requestedPlatform} />
            </div>

            <div className="space-y-2">
              {brandInput && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Search className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Brand:</span>
                  <span className="text-gray-900">{brandInput}</span>
                </div>
              )}

              {getDomain(companyUrl || url) && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <ArrowUpRight className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Domain:</span>
                  <span className="break-all text-gray-900">{getDomain(companyUrl || url)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Score Dashboard */}
      <div className="grid gap-4 md:grid-cols-3">
        <ScoreCard
          title="Brand Awareness"
          score={brandAwarenessScore}
          category={brandAwarenessCategory}
          description="Overall brand recognition and visibility"
          icon={<TrendingUp className="h-4 w-4" />}
          type="awareness"
        />

        <ScoreCard
          title="Sentiment"
          category={sentimentClassification}
          description="Public perception and sentiment analysis"
          icon={<Heart className="h-4 w-4" />}
          type="sentiment"
        />

        <ScoreCard
          title="Credibility"
          category={credibilityClassification}
          description="Trust and authority assessment"
          icon={<Shield className="h-4 w-4" />}
          type="credibility"
        />
      </div>

      {/* Key Metrics Section */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Awareness Sources */}
        {brandAwarenessSources && brandAwarenessSources.length > 0 && (
          <div className="rounded-2xl border border-gray-200/50 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Awareness Sources</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {brandAwarenessSources.slice(0, 8).map((source: string, index: number) => (
                <MetricPill key={index} label={source} color="blue" variant="default" size="sm" />
              ))}
              {brandAwarenessSources.length > 8 && (
                <MetricPill
                  label={`+${brandAwarenessSources.length - 8} more`}
                  color="gray"
                  variant="outline"
                  size="sm"
                />
              )}
            </div>
          </div>
        )}

        {/* Products & Services */}
        {servicesProducts && servicesProducts.length > 0 && (
          <div className="rounded-2xl border border-gray-200/50 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <Tag className="h-4 w-4 text-green-600" />
              <h3 className="text-sm font-semibold text-gray-900">Products & Services</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {servicesProducts.slice(0, 6).map((item: string, index: number) => (
                <MetricPill key={index} label={item} color="green" variant="default" size="sm" />
              ))}
              {servicesProducts.length > 6 && (
                <MetricPill
                  label={`+${servicesProducts.length - 6} more`}
                  color="gray"
                  variant="outline"
                  size="sm"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Insights Section */}
      <div className="space-y-4">
        {/* Brand Description */}
        {brandDescription && (
          <div className="rounded-2xl border border-gray-200/50 bg-white p-6 shadow-sm">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Bot className="h-4 w-4 text-purple-600" />
              Brand Description
            </h3>
            <div className="rounded-xl bg-gray-50/80 p-4">
              <p className="text-sm leading-relaxed text-gray-700">{brandDescription}</p>
            </div>
          </div>
        )}

        {/* Related Prompts */}
        {relatedPrompts && relatedPrompts.length > 0 && (
          <div className="rounded-2xl border border-gray-200/50 bg-white p-6 shadow-sm">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
              <MessageSquare className="h-4 w-4 text-orange-600" />
              Related AI Prompts
            </h3>
            <div className="space-y-2">
              {relatedPrompts.slice(0, 5).map((prompt: string, index: number) => (
                <div
                  key={index}
                  className="rounded-lg border border-gray-200/60 bg-gray-50/50 px-3 py-2"
                >
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-400">&ldquo;</span>
                    {prompt}
                    <span className="text-gray-400">&rdquo;</span>
                  </p>
                </div>
              ))}
              {relatedPrompts.length > 5 && (
                <div className="pt-2 text-center">
                  <span className="text-xs text-gray-500">
                    +{relatedPrompts.length - 5} more prompts
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function downloadAiVisibility(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function aiVisibilityToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(aiVisibility, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  // Handle both array and object results
  const dataItems = Array.isArray(result) ? result : [result]
  const data = dataItems[0] // Display first item, but preserve all data

  // Determine mode based on result structure
  const mode = inputs.mode || 'brand_report'
  const isScrapeMode = mode === 'scrape' || !!(data as any)?.extracted_content

  if (isScrapeMode) {
    // Scrape mode markdown export
    const query = (data as any)?.query || inputs.query || ''
    const extractedContent = (data as any)?.extracted_content || ''
    const contentType = (data as any)?.content_type || 'text'
    const wordCount = (data as any)?.word_count || 0
    const hasCodeBlocks = (data as any)?.has_code_blocks || false
    const hasLinks = (data as any)?.has_links || false
    const extractedAt = (data as any)?.extracted_at || ''
    const requestedPlatform = (data as any)?.requestedPlatform || 'ChatGPT'

    return `## ChatGPT Answer

**Query:** ${query}
**AI Platform:** ${requestedPlatform}
**Content Type:** ${contentType}
**Word Count:** ${wordCount}
**Features:** ${[hasCodeBlocks && 'Code Blocks', hasLinks && 'External Links'].filter(Boolean).join(', ') || 'Plain Text'}
${extractedAt ? `**Extracted:** ${new Date(extractedAt).toLocaleString()}` : ''}

### Extracted Content

${extractedContent}

---
`
  } else {
    // Brand report mode markdown export
    const url = inputs.url || inputs.website || inputs.domain || ''
    const brandAwarenessScore = (data as any)?.brand_awareness_score || 0
    const brandAwarenessCategory = (data as any)?.brand_awareness_category || ''
    const brandAwarenessSources = (data as any)?.brand_awareness_sources || []
    const brandDescription = (data as any)?.brand_description || ''
    const sentimentClassification =
      (data as any)?.sentiment_classification || (data as any)?.sentiment_category || ''
    const credibilityClassification =
      (data as any)?.credibility_classification || (data as any)?.credibility_category || ''
    const servicesProducts = (data as any)?.services_products || []
    const relatedPrompts = (data as any)?.related_prompts || []
    const requestedPlatform = (data as any)?.requestedPlatform || inputs?.aiTool || 'ChatGPT'

    return `## AI Brand Awareness Analysis

**URL:** ${url}
**AI Platform:** ${requestedPlatform}
**Brand Awareness Score:** ${brandAwarenessScore}/100
${brandAwarenessCategory ? `**Category:** ${brandAwarenessCategory}` : ''}

### Classifications
${sentimentClassification ? `**Sentiment:** ${sentimentClassification}` : ''}
${credibilityClassification ? `**Credibility:** ${credibilityClassification}` : ''}

${
  brandDescription
    ? `### Brand Description
${brandDescription}

`
    : ''
}${
      brandAwarenessSources.length > 0
        ? `### Awareness Sources
${brandAwarenessSources.map((source: string) => `- ${source}`).join('\n')}

`
        : ''
    }${
      servicesProducts.length > 0
        ? `### Services & Products
${servicesProducts.map((item: string) => `- ${item}`).join('\n')}

`
        : ''
    }${
      relatedPrompts.length > 0
        ? `### Related AI Prompts
${relatedPrompts.map((prompt: string) => `- "${prompt}"`).join('\n')}

`
        : ''
    }---
`
  }
}

export const AiVisibilityDisplay = createWorkflowToolComponent(
  AiVisibilityDisplayComponent,
  downloadAiVisibility,
  aiVisibilityToMarkdown
)
